import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import SlButton from '@shoelace-style/shoelace/dist/react/button';

import { downloadFacilitatorGuide, type FacilitatorGuideFormat } from '../../api';
import {
  useHeaderFooter, useFacilitatorGuide, useFacilitatorStatus, useMyCohorts,
} from '../../hooks';

import messages from './messages';
import { getGuideLanguage } from './utils';

import './index.scss';

import heroImg from '../../img/cohorts/COHORTS-LWC.jpg';

const START_HINT_ID = 'cohorts-start-hint';

interface CohortsProps {
  children?: React.ReactNode;
}

const Cohorts: React.FC<CohortsProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  // Both guide and facilitator-status hooks fail closed for an anonymous caller without
  // saying so, so the signed-out/learner distinction is made here rather than in either hook.
  const authenticatedUser = getAuthenticatedUser();

  const { headerContent, footerContent } = useHeaderFooter(children);
  const { status, canCreateCohorts, loaded: statusLoaded } = useFacilitatorStatus();
  const { pdfAvailable, pptxAvailable, loaded: guideLoaded } = useFacilitatorGuide();
  // Every cohort the caller belongs to; skipped for anonymous so the public route stays one render.
  const {
    cohorts, loaded: cohortsLoaded, failed: cohortsFailed, hasMore: cohortsHaveMore,
  } = useMyCohorts(Boolean(authenticatedUser));
  const [downloadingFormat, setDownloadingFormat] = useState<FacilitatorGuideFormat | null>(null);
  const [downloadFailed, setDownloadFailed] = useState<boolean>(false);

  const signInHref = `/login?redirect-to=${window.location.href}`;
  // On every API path a co-facilitator moderates a cohort: the plugin drops the role row with the last one.
  const isCoFacilitator = status?.facilitator_type === 'co_facilitator';
  const hasACohort = cohorts.length > 0 || (cohortsFailed && isCoFacilitator);
  // Exactly one cohort, with no further page: open it directly instead of a one-row list.
  const onlyCohort = cohorts.length === 1 && !cohortsHaveMore ? cohorts[0] : null;
  const cohortActionResolved = statusLoaded && (!authenticatedUser || cohortsLoaded);

  const handleGuideDownload = async (format: FacilitatorGuideFormat): Promise<void> => {
    setDownloadFailed(false);
    setDownloadingFormat(format);
    try {
      await downloadFacilitatorGuide(getGuideLanguage(), format);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloadingFormat(null);
    }
  };

  // `aria-disabled`, not SlButton's native `disabled` (which sets tabindex="-1"): the locked
  // button stays focusable and announces its hint while the guarded onClick keeps it inert.
  const handleStartCohortClick = (): void => {
    if (!canCreateCohorts) {
      return;
    }
    navigate('/cohorts/mine');
  };

  const handleMyCohortsClick = (): void => {
    navigate(onlyCohort ? `/cohorts/${onlyCohort.slug}` : '/cohorts/mine');
  };

  // Native <button>, not `<a href download>`: the `download` attribute is
  // ignored cross-origin, so an error response would navigate to the raw JSON.
  const guideDownloadButton = (
    format: FacilitatorGuideFormat,
    label: typeof messages.downloadPdfButton,
  ): React.ReactNode => (
    <button
      type="button"
      className="leg-primary-button"
      disabled={downloadingFormat === format}
      onClick={() => handleGuideDownload(format)}
    >
      {intl.formatMessage(label)}
    </button>
  );

  const startCohortButton = (
    <SlButton
      className={`leg-primary-button cohorts-start-button${canCreateCohorts ? '' : ' is-disabled'}`}
      aria-disabled={canCreateCohorts ? 'false' : 'true'}
      aria-describedby={canCreateCohorts ? undefined : START_HINT_ID}
      onClick={handleStartCohortClick}
    >
      {intl.formatMessage(messages.startCohortButton)}
    </SlButton>
  );

  const myCohortsButton = (
    <SlButton
      className="leg-primary-button cohorts-start-button"
      onClick={handleMyCohortsClick}
    >
      {intl.formatMessage(messages.myCohortsButton)}
    </SlButton>
  );

  const signInButton = (label: typeof messages.guideSignInButton): React.ReactNode => (
    <div className="leg-primary-button">
      <NavLink to={signInHref}>{intl.formatMessage(label)}</NavLink>
    </div>
  );

  // The anonymous check comes first and wins over both fetched results.
  const renderGuideCardBody = (): React.ReactNode => {
    if (!guideLoaded) {
      return null;
    }
    if (pdfAvailable || pptxAvailable) {
      return (
        <>
          <div className="cohorts-card-actions">
            {pdfAvailable && guideDownloadButton('pdf', messages.downloadPdfButton)}
            {pptxAvailable && guideDownloadButton('pptx', messages.downloadPptxButton)}
          </div>
          {downloadFailed && (
            <p className="form-error show cohorts-guide-error">
              {intl.formatMessage(messages.guideDownloadError)}
            </p>
          )}
        </>
      );
    }
    if (!authenticatedUser) {
      return <div className="cohorts-card-actions">{signInButton(messages.guideSignInButton)}</div>;
    }
    return (
      <p className="leg-p cohorts-guide-empty">
        {intl.formatMessage(messages.guideUnavailable)}
      </p>
    );
  };

  // Belonging to a cohort and being allowed to create one are independent: both controls can show.
  const renderStartCohortAction = (): React.ReactNode => {
    if (!authenticatedUser) {
      return signInButton(messages.startCohortSignInButton);
    }
    return (
      <>
        {hasACohort && myCohortsButton}
        {startCohortButton}
        {!canCreateCohorts && (
          <p id={START_HINT_ID} className="cohorts-start-hint">
            {intl.formatMessage(messages.startCohortLockedHint)}
          </p>
        )}
      </>
    );
  };

  return (
    <>
      <input type="hidden" id="anPageName" name="page" defaultValue="cohorts" />
      <div className="container-center-horizontal">
        <div className="about screen cohorts">
          <div className="homepage">
            {headerContent}
            <div className="overlap-group6">
              <div className="hero-image">
                <div className="overlap-group-1">
                  <div className="grad" />
                  <img
                    className="overlap-group-item"
                    src={heroImg}
                    alt=""
                  />
                  <h1 className="cohorts-hero-title">
                    <span
                      className="recklessneue-regular-normal-white-100-1px cohorts-hero-eyebrow"
                      style={{ fontStyle: 'italic' }}
                    >
                      {intl.formatMessage(messages.heroTitleLead)}
                    </span>
                    <span className="instrumentserif-normal-white-100-1px cohorts-hero-name">
                      {intl.formatMessage(messages.heroTitleCohorts)}
                    </span>
                  </h1>
                  <p className="instrumentsans-normal-white-26px cohorts-hero-subtitle">
                    {intl.formatMessage(messages.heroSubtitle)}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="legatum-container">
            <div className="c-section">
              <div className="leg-title">{intl.formatMessage(messages.introTitle)}</div>
              <p className="leg-p">{intl.formatMessage(messages.introText1)}</p>
              <p className="leg-p">{intl.formatMessage(messages.introText2)}</p>
            </div>
            <div className="c-section cohorts-cards">
              <div className="overview-box">
                <div className="overview-text">
                  <div className="ov-title">{intl.formatMessage(messages.guideCardTitle)}</div>
                  <p className="leg-p">{intl.formatMessage(messages.guideCardText)}</p>
                  {/* No actions until availability is known — avoids a flash of
                      dead buttons that would 404. */}
                  {renderGuideCardBody()}
                </div>
              </div>
              <div className="overview-box">
                <div className="overview-text">
                  <div className="ov-title">{intl.formatMessage(messages.startCardTitle)}</div>
                  <p className="leg-p">{intl.formatMessage(messages.startCardText)}</p>
                  {/* No control until both results are in — the label must not change later. */}
                  {cohortActionResolved && (
                    <div className="cohorts-card-actions">
                      {renderStartCohortAction()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {footerContent}
        </div>
      </div>
    </>
  );
};

export default Cohorts;
